import { allowedMime } from "@api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import WarningIcon from "@mui/icons-material/WarningAmber";
import { Alert, Button, IconButton, LinearProgress, Stack, Typography } from "@mui/material";
import { TypoTooltip } from "@styleguide";
import { type ChangeEvent, type RefObject, useRef, useState } from "react";
import { type Control, useFieldArray, useWatch } from "react-hook-form";
import type { IModalTrainingApplyForm } from "../../ModalTrainingApply/types";

interface ITrainingSharedFormDocumentsProps {
  control: Control<IModalTrainingApplyForm>;
  loading: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  documentsProgress: { currentStep: number; totalSteps: number } | null;
}

const fileInputAccept = (allowedMime ?? []).join(",");

export const TrainingSharedFormDocuments = ({
  control,
  loading,
  documentsProgress,
  inputRef,
}: ITrainingSharedFormDocumentsProps) => {
  const internalInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = inputRef ?? internalInputRef;
  const [warning, setWarning] = useState(false);
  const {
    fields: documents,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "documents",
  });
  const watchedDocuments = useWatch({ control, name: "documents" }) ?? [];

  const handleOpenFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) {
      return;
    }

    setWarning(documents.length + files.length > 5);
    const nextDocuments: IModalTrainingApplyForm["documents"] = [];

    for (let index = 0; index < 5 - documents.length && index < files.length; index += 1) {
      if (files[index].size <= 5242880) {
        nextDocuments.push({ file: files[index] });
      } else {
        setWarning(true);
      }
    }

    append(nextDocuments);
    event.target.value = "";
  };

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="body1SemiBold">Documents</Typography>
      <Alert color="warning" icon={<WarningIcon />} {...(!warning && { sx: { display: "none" } })}>
        Vous ne pouvez importer que 5 fichiers maximum
        <br />
        La taille maximal d'un fichier est de 5Mo
      </Alert>
      {documentsProgress && documentsProgress.totalSteps > 0 && (
        <LinearProgress
          variant="determinate"
          value={(documentsProgress.currentStep / documentsProgress.totalSteps) * 100}
        />
      )}
      <Stack
        direction="row"
        sx={{
          alignItems: "start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <input
          type="file"
          style={{ display: "none" }}
          onChange={handleChange}
          accept={fileInputAccept}
          multiple
          ref={fileInputRef}
        />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenFileInput}
          size="small"
          sx={{ flex: "0 0 auto", textTransform: "unset" }}
          disabled={Boolean(documentsProgress) || loading || documents.length >= 5}
        >
          <Typography variant="body1SemiBold">Ajouter un document</Typography>
        </Button>
        <Stack
          sx={{
            alignItems: "end",
            overflow: "hidden",
          }}
        >
          {documents.map((document, index) => (
            <Stack
              key={document.id}
              direction="row"
              sx={{
                alignItems: "center",
                gap: 0.5,
                maxWidth: "100%",
              }}
            >
              <TypoTooltip variant="caption" noWrap>
                {watchedDocuments[index]?.info?.name ?? watchedDocuments[index]?.file?.name}
              </TypoTooltip>
              <IconButton
                size="small"
                sx={{ flex: "0 0 auto" }}
                onClick={() => {
                  setWarning(false);
                  remove(index);
                }}
                disabled={Boolean(documentsProgress) || loading}
              >
                <DeleteIcon color="primary" fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
